import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Progress, Select, Tag } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import {
  capitalize,
  floor,
  startCase,
  toLower
} from '../../../../util/lodashlike';
import {
  AlertScoreWrapper,
  ProgressText,
  LoadingWrapper,
  SmartAssessHeaderPanel,
  Title,
  SelectBoxContainer,
  StyledControlWithLabel,
  StyledCmControls,
  ArrowButton,
  EventContextStrip,
  EventContextLabel,
  ContextBadge,
  ContextArrows
} from './styled';
import Wait4Me from '../../../../components/Wait4Me';
import { normalizeAlertScore } from '../../../../util/normalization';
import FlagRed from '../../../../assets/flag_Red.svg';
import FlagOrange from '../../../../assets/flag_Orange.svg';
import { TM_FRAUD_SOURCE_IDS } from '../../../../util/GlobalConstant';
import {
  convertISOToDateTimeWithoutSeconds,
  convertISOToDateFormat
} from '../../../../util/timezone';

import HeaderPanel from './HeaderPanel';
import HeaderCounterPanel from './HeaderCounterPanel';
import useAlertLevelConfigs from '../../../common/hooks/useAlertLevelConfig';

// Same palette as the run-instance listing Score/Level columns
// (PreventionResultTable.js) so the detail header matches the table.
const severityColorMap = { High: '#ff4d4f', Medium: '#faad14', Low: '#52c41a' };
const levelTagColorMap = { High: 'red', Medium: 'orange', Low: 'green' };

const defaultProps = {
  alertDetails: { data: null, isLoading: false },
  alertHeader: null,
  disableSelect: false,
  onChangeAlertQuality: null
};

// FIN-11542 Cluster 3 (Ganesh 2026-06-03): GS hide/rename plumbing only.
// Metadata-field stuffing (Cluster 1+2) reverted 2026-06-03 — Kiran metadata
// table belongs to the Entity-Map-triggered side panel (Spatial OUT), not the
// v6.3.4 Tx Details / Activity Details cards.
// `cardConfig` (optional prop) shape:
//   {
//     transactionDetails: { hide: [...], rename: { 'ID': 'Txn ID' } },
//     transactionAmount:  { hide: [...], rename: {...} },
//     activityDetails:    { hide: [...], rename: {...} },
//     activityStatus:     { hide: [...], rename: {...} }
//   }
// Caller (SmartAssessmentPageHeader / parent) wires this from Global Settings.
// Default = empty object → no hide, no rename (backward compatible).
const applyCardConfig = (data, cardCfg) => {
  if (!cardCfg) return data;
  const hideSet = new Set(cardCfg.hide || []);
  const renameMap = cardCfg.rename || {};
  return data
    .filter((item) => {
      if (!item) return false;
      // strip trailing ":" from label for hide-key matching
      const key = (item.fieldLabel || '').replace(/:\s*$/, '');
      return !hideSet.has(key);
    })
    .map((item) => {
      const key = (item.fieldLabel || '').replace(/:\s*$/, '');
      if (renameMap[key]) {
        return { ...item, fieldLabel: `${renameMap[key]}:` };
      }
      return item;
    });
};

const AlertTopPanelDetails = ({
  alertDetails,
  alertHeader,
  isPrevention,
  isFromCM,
  alertMetaSuffix,
  alertLevels,
  trmActionsData,
  cardConfig,
  ...props
}) => {
  const { getLevelColor } = useAlertLevelConfigs(alertLevels);
  const { data = null, isLoading } = alertDetails;
  const [cardIdx, setCardIdx] = useState(0);
  const alertQualityList = [
    { id: 'high', name: 'High Quality', color: getLevelColor('High') },
    { id: 'good', name: 'Good Quality', color: getLevelColor('Good') },
    { id: 'low', name: 'Low Quality', color: getLevelColor('Low') }
  ];

  let sampleTransactionDetailsHeaderData = [
    { fieldLabel: 'ID:', fieldValue: '-' },
    { fieldLabel: 'Timestamp:', fieldValue: '-' },
    { fieldLabel: 'Type:', fieldValue: '-' },
    { fieldLabel: 'Category:', fieldValue: '-' }
  ];

  let sampleTransactionAmountHeaderData = [
    { fieldLabel: 'Source Amount:', fieldValue: 'USD 1920' },
    { fieldLabel: 'Source (USD):', fieldValue: 'USD 1920' },
    { fieldLabel: 'Destination Amount:', fieldValue: 'MYR 9000' }
  ];

  let activityDetailsHeaderData = [
    ...sampleTransactionDetailsHeaderData.slice(0, -1),
    { fieldLabel: 'Sub Type:', fieldValue: '-' }
  ];

  let activityStatusHeaderData = [
    { fieldLabel: 'Activity Status:', fieldValue: '-' },
    { fieldLabel: 'Activity Remark:', fieldValue: '-' },
    { fieldLabel: 'IP Address:', fieldValue: '-' },
    { fieldLabel: 'Channel:', fieldValue: '-' }
  ];

  let sampleCounterData = [];
  let totalOfSampleData = 0;

  // FIN-12133: colour-code the Action by its configured severity's level colour
  // (action_severity -> alert_level_details), consistent with the Score Explanation.
  const preventionAlertColors = useCallback(
    (key) =>
      getLevelColor(
        trmActionsData?.find(
          ({ displayName }) => toLower(displayName) === toLower(key)
        )?.actionSeverity
      ),
    [trmActionsData, getLevelColor]
  );

  if (data) {
    const {
      txnDateTime,
      origTxnAmount,
      origCurrencyCode,
      oppCurrencyCode,
      oppTxnAmount,
      txnCurrency,
      txnAmount,
      transactionKey,
      transactionTypeCode,
      transactionTypeDescription,
      triggeredRulesActionSummary,
      additionalInfo
    } = data;

    sampleTransactionDetailsHeaderData = [
      { fieldLabel: 'ID:', fieldValue: transactionKey ? transactionKey : '-' },
      {
        fieldLabel: 'Timestamp:',
        fieldValue: txnDateTime
          ? convertISOToDateTimeWithoutSeconds(txnDateTime)
          : '-'
      },
      { fieldLabel: 'Type:', fieldValue: transactionTypeCode || '-' },
      { fieldLabel: 'Category:', fieldValue: transactionTypeDescription || '-' }
    ];

    sampleTransactionAmountHeaderData = [
      {
        fieldLabel: 'Source Amount:',
        fieldValue: origTxnAmount
          ? `${origTxnAmount} ${origCurrencyCode || ''}`
          : '-'
      },
      {
        // FIN-11542 P3 (B6 TA-1): mock parity — relabel to "Source (USD):".
        fieldLabel: 'Source (USD):',
        fieldValue: txnAmount ? `${txnAmount} ${txnCurrency || ''}` : '-'
      },
      {
        fieldLabel: 'Destination Amount:',
        fieldValue: oppTxnAmount
          ? `${oppTxnAmount} ${oppCurrencyCode || ''}`
          : '-'
      }
    ];

    // FIN-11542 TD-1 (Ganesh 2026-06-02): always build Activity Details +
    // Activity Status data, regardless of `typeOfAlert`. Cards always render
    // in the 4-card cycle; placeholder "-" values flow through when
    // additionalInfo is missing / non-JSON / lacks activity fields.
    let activityInfo = {};
    try {
      activityInfo = additionalInfo ? JSON.parse(additionalInfo) : {};
      if (activityInfo == null || typeof activityInfo !== 'object') {
        activityInfo = {};
      }
    } catch {
      activityInfo = {};
    }
    const {
      activity_id = null,
      activity_datetime = null,
      activity_description = null,
      activity_status = null,
      activity_subtype = null,
      activity_type = null,
      ip_address = null,
      channel = null
    } = activityInfo;

    activityDetailsHeaderData = [
      { fieldLabel: 'ID:', fieldValue: activity_id },
      { fieldLabel: 'Timestamp:', fieldValue: activity_datetime },
      { fieldLabel: 'Type:', fieldValue: activity_type },
      { fieldLabel: 'Sub Type:', fieldValue: activity_subtype }
    ];

    activityStatusHeaderData = [
      { fieldLabel: 'Activity Status:', fieldValue: activity_status },
      { fieldLabel: 'Activity Remark:', fieldValue: activity_description },
      { fieldLabel: 'IP Address:', fieldValue: ip_address },
      // FIN-11542 P3 (B6 TC_AS_005/011): mock + Kiran spec — Channel field
      // on the Activity Status card.
      { fieldLabel: 'Channel:', fieldValue: channel || '-' }
    ];

    try {
      const counterData = JSON.parse(triggeredRulesActionSummary);

      Object.keys(counterData).forEach((counterLabel) => {
        totalOfSampleData += counterData[counterLabel];
        // FIN-13963: the action name is title-cased HERE, not by the tile's
        // CSS. It used to be `.toLowerCase()` + `text-transform: capitalize`
        // on `.counterLabel` — a transform split across two layers, so a
        // stylesheet that did not reach the span left the lowercase half
        // showing on its own ("soft stop"). `startCase` also splits on `_`,
        // which CSS capitalize cannot (SOFT_STOP -> "Soft_stop").
        // `nowrap` keeps multi-word actions whole: the tile is narrow, so
        // "Soft Stop" used to break between its two words.
        const label = (
          <>
            Action:{' '}
            <span
              style={{
                color: preventionAlertColors(counterLabel),
                whiteSpace: 'nowrap'
              }}>
              {startCase(counterLabel)}
            </span>
          </>
        );

        sampleCounterData.push({
          labelText: label,
          valueText: counterData[counterLabel]
        });
      });
    } catch {
      console.log('Error parsing triggeredRulesActionSummary');
    }
  }

  if (isPrevention) {
    return (
      <>
        {!isFromCM && alertHeader && (
          <SmartAssessHeaderPanel>
            {alertHeader}
            {data?.alertSourceId === TM_FRAUD_SOURCE_IDS.TM && (
              <span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <img
                  alt=""
                  style={{ height: '26px', paddingBottom: '2px' }}
                  src={FlagOrange}
                />
                &nbsp; AML-TM Detection
              </span>
            )}
            {data?.alertSourceId === TM_FRAUD_SOURCE_IDS.FRAUD && (
              <span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <img
                  alt=""
                  style={{ height: '26px', paddingBottom: '2px' }}
                  src={FlagRed}
                />
                &nbsp; Fraud Detection
              </span>
            )}

            {data?.alertSourceId !== TM_FRAUD_SOURCE_IDS.TM &&
              data?.alertSourceId !== TM_FRAUD_SOURCE_IDS.FRAUD && (
                <span>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <img
                    alt=""
                    style={{ height: '26px', paddingBottom: '2px' }}
                    src={FlagRed}
                  />
                  &nbsp; Fraud Prevention
                </span>
              )}
          </SmartAssessHeaderPanel>
        )}

        {isLoading ? (
          <LoadingWrapper>
            <Wait4Me />
          </LoadingWrapper>
        ) : (
          <>
            {isFromCM && (
              <StyledCmControls>
                <StyledControlWithLabel label="Alert ID">
                  {data?.alertId}
                </StyledControlWithLabel>
                <StyledControlWithLabel label="Alert Source">
                  Fraud Prevention
                </StyledControlWithLabel>
                <StyledControlWithLabel label="Alert Date">
                  {convertISOToDateFormat(data?.alertDate)}
                </StyledControlWithLabel>
                {alertMetaSuffix}
              </StyledCmControls>
            )}
            {(() => {
              // FIN-11962 — two independent decisions:
              //
              // (1) PILL LABEL = count-driven. StarRocks triggered_transactions /
              //     triggered_activity counts on the alert-detail response decide the
              //     badge: both>0 -> "Transaction · Activity"; activity>0 only ->
              //     "Activity"; else -> "Transaction". This summarises WHAT TRIGGERED.
              //
              // (2) CARD PANES + ARROWS = data-driven from unified_alerts. type_of_alert
              //     picks the primary pane; additionalInfo presence (hasActivityData)
              //     adds the Activity pane; a present transaction_key adds the
              //     Transaction pane. Arrows show only when BOTH panes exist. This
              //     reflects WHAT DATA WE ACTUALLY HAVE — so the pill can read
              //     "Transaction · Activity" while only the populated pane(s) render.
              const txnTriggeredCount = Number.isFinite(
                data?.triggeredTransactionCount
              )
                ? data.triggeredTransactionCount
                : null;
              const activityTriggeredCount = Number.isFinite(
                data?.triggeredActivityCount
              )
                ? data.triggeredActivityCount
                : null;
              const labelHasTxn = (txnTriggeredCount || 0) > 0;
              const labelHasActivity = (activityTriggeredCount || 0) > 0;
              const contextLabel =
                labelHasTxn && labelHasActivity
                  ? 'Transaction · Activity'
                  : labelHasActivity
                  ? 'Activity'
                  : 'Transaction';

              const transactionContext = {
                context: 'transaction',
                cards: [
                  {
                    title: 'Transaction Details',
                    icon: <FileTextOutlined />,
                    data: applyCardConfig(
                      sampleTransactionDetailsHeaderData,
                      cardConfig?.transactionDetails
                    )
                  },
                  {
                    title: 'Transaction Amount',
                    icon: <ThunderboltOutlined />,
                    data: applyCardConfig(
                      sampleTransactionAmountHeaderData,
                      cardConfig?.transactionAmount
                    )
                  }
                ]
              };
              const activityContext = {
                context: 'activity',
                cards: [
                  {
                    title: 'Activity Details',
                    icon: <UserAddOutlined />,
                    data: applyCardConfig(
                      activityDetailsHeaderData,
                      cardConfig?.activityDetails
                    )
                  },
                  {
                    title: 'Activity Status',
                    icon: <SafetyOutlined />,
                    data: applyCardConfig(
                      activityStatusHeaderData,
                      cardConfig?.activityStatus
                    )
                  }
                ]
              };
              // An alert is created by exactly one event type, so exactly one
              // pane renders, chosen by typeOfAlert: 'activity' -> Activity pane;
              // otherwise (transaction / absent) -> Transaction pane. additionalInfo
              // is only populated for activity alerts. A combined (both-pane) alert
              // does not occur in real pipeline data, so the carousel below stays
              // dormant (showArrows is false for a single pane) -- the prev/next
              // wiring is kept intact for safety only. The pill label is
              // count-driven and computed separately above.
              const isActivityAlert =
                toLower(data?.typeOfAlert || '') === 'activity';
              const navigableCards = isActivityAlert
                ? [activityContext]
                : [transactionContext];

              const showArrows = navigableCards.length > 1;

              const safeIdx = Math.min(cardIdx, navigableCards.length - 1);
              const visiblePair = navigableCards[safeIdx];
              // FIN-11962: render the EVENT CONTEXT strip (label + badge + arrows)
              // directly above the card pair so the arrows sit at the top-right
              // corner of the 2nd card (Kiran's mock). A hidden copy of the strip
              // tops the counters / alert-score columns so they stay aligned with
              // the cards regardless of strip height (arrows present or not).
              const renderStrip = (hidden) => (
                <EventContextStrip
                  aria-hidden={hidden || undefined}
                  style={hidden ? { visibility: 'hidden' } : undefined}>
                  <EventContextLabel>
                    {hidden ? ' ' : 'EVENT CONTEXT'}
                    <ContextBadge>{hidden ? ' ' : contextLabel}</ContextBadge>
                  </EventContextLabel>
                  {showArrows && (
                    <ContextArrows>
                      <ArrowButton
                        type="button"
                        aria-label="Previous card"
                        disabled={safeIdx === 0}
                        onClick={() => setCardIdx((i) => Math.max(0, i - 1))}>
                        <LeftOutlined />
                      </ArrowButton>
                      <ArrowButton
                        type="button"
                        aria-label="Next card"
                        disabled={safeIdx === navigableCards.length - 1}
                        onClick={() =>
                          setCardIdx((i) =>
                            Math.min(navigableCards.length - 1, i + 1)
                          )
                        }>
                        <RightOutlined />
                      </ArrowButton>
                    </ContextArrows>
                  )}
                </EventContextStrip>
              );
              return (
                <Row gutter={[20, 10]} justify="start" wrap={false} align="top">
                  {/* LEFT: EVENT CONTEXT strip + the card pair. flex-grows so the
                      strip width == the card-pair width; the arrows (space-between)
                      land at the 2nd card's top-right corner. */}
                  <Col flex="1 1 0">
                    {renderStrip(false)}
                    <Row gutter={[20, 10]} wrap={false} align="stretch">
                      {visiblePair.cards.map((card) => (
                        <Col
                          key={card.title}
                          flex="1 1 0"
                          style={{ display: 'flex' }}>
                          <HeaderPanel
                            title={card.title}
                            icon={card.icon}
                            data={card.data}
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>

                  {/* Counter tiles — hidden strip spacer keeps them aligned with the cards. */}
                  <Col flex="none">
                    {renderStrip(true)}
                    <Row gutter={[20, 10]} wrap={false}>
                      <Col flex="none">
                        <HeaderCounterPanel
                          data={{
                            labelText: 'Variants Breached',
                            valueText: totalOfSampleData
                          }}
                        />
                      </Col>
                      {sampleCounterData.map((counterData, counterIndex) => {
                        return (
                          <Col flex="none" key={counterIndex}>
                            <HeaderCounterPanel data={counterData} />
                          </Col>
                        );
                      })}
                    </Row>
                  </Col>
                  {/* Triage controls column — Alert Score on top (primary
                        triage signal), Alert Quality stacked below it. Alert
                        Quality stays `!isFromCM`-gated per user direction
                        2026-06-04 ("don't fix") — visible on the Run Instance
                        (FRAML) route only, hidden in CM context. */}
                  <Col flex="none">
                    {renderStrip(true)}
                    {data?.alertScore != null && (
                      <>
                        <Title>
                          Alert Score{' '}
                          <Tag
                            color={
                              levelTagColorMap[capitalize(data?.alertLevel)] ||
                              'default'
                            }>
                            {data?.alertLevelLabel ||
                              capitalize(data?.alertLevel)}
                          </Tag>
                        </Title>
                        <AlertScoreWrapper style={{ width: 165 }}>
                          <Progress
                            percent={data?.alertScore}
                            showInfo={false}
                            strokeColor={
                              data?.alertLevelColor ||
                              severityColorMap[capitalize(data?.alertLevel)] ||
                              severityColorMap.Low
                            }
                          />
                          <ProgressText>
                            <p>
                              <b>
                                {normalizeAlertScore(
                                  floor(data?.alertScore, 2)
                                )}
                              </b>
                              /100
                            </p>
                          </ProgressText>
                        </AlertScoreWrapper>
                      </>
                    )}
                    {!isFromCM && (
                      <div
                        style={{
                          marginTop: data?.alertScore != null ? 10 : 0
                        }}>
                        <Title>Alert Quality</Title>
                        <SelectBoxContainer>
                          <Select
                            style={{ minWidth: '165px' }}
                            disabled={isLoading || props?.disableSelect}
                            defaultValue={
                              data?.alertQuality && toLower(data?.alertQuality)
                            }
                            placeholder="Please Select"
                            onChange={(value) =>
                              props?.onChangeAlertQuality(
                                value,
                                data?.alertSourceId
                              )
                            }>
                            {alertQualityList?.map(({ id, name, color }) => (
                              <Select.Option
                                key={id}
                                value={id}
                                style={{ color }}>
                                {name}
                              </Select.Option>
                            ))}
                          </Select>
                        </SelectBoxContainer>
                      </div>
                    )}
                  </Col>
                </Row>
              );
            })()}
          </>
        )}
      </>
    );
  } else {
    return (
      <>
        {alertHeader ? (
          <SmartAssessHeaderPanel>
            {alertHeader}
            {data?.alertSourceId === TM_FRAUD_SOURCE_IDS.TM && (
              <span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <img
                  alt=""
                  style={{ height: '26px', paddingBottom: '2px' }}
                  src={FlagOrange}
                />
                &nbsp; AML-TM Detection
              </span>
            )}
            {data?.alertSourceId === TM_FRAUD_SOURCE_IDS.FRAUD && (
              <span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <img
                  alt=""
                  style={{ height: '26px', paddingBottom: '2px' }}
                  src={FlagRed}
                />
                &nbsp; Fraud Detection
              </span>
            )}
          </SmartAssessHeaderPanel>
        ) : null}

        {isLoading ? (
          <LoadingWrapper>
            <Wait4Me />
          </LoadingWrapper>
        ) : (
          <Row gutter={[40, 20]} justify="start" wrap={false}>
            <Col>
              <Title>Alert Score</Title>
              <AlertScoreWrapper>
                <Progress
                  percent={data?.alertScore}
                  showInfo={false}
                  strokeColor={data?.alertLevelColor}
                />
                <ProgressText>
                  <p>
                    <b>
                      {data?.alertLevelLabel || capitalize(data?.alertLevel)}{' '}
                      {normalizeAlertScore(floor(data?.alertScore, 2))}
                    </b>
                    /100
                  </p>
                </ProgressText>
              </AlertScoreWrapper>
            </Col>

            <Col>
              <Title>Alert Quality</Title>
              <SelectBoxContainer>
                <Select
                  style={{ minWidth: '165px' }}
                  disabled={isLoading || props?.disableSelect}
                  defaultValue={
                    data?.alertQuality && toLower(data?.alertQuality)
                  }
                  placeholder="Please Select"
                  onChange={(value) =>
                    props?.onChangeAlertQuality(value, data?.alertSourceId)
                  }>
                  {alertQualityList?.map(({ id, name, color }) => (
                    <Select.Option key={id} value={id} style={{ color }}>
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              </SelectBoxContainer>
            </Col>
          </Row>
        )}
      </>
    );
  }
};

AlertTopPanelDetails.defaultProps = { ...defaultProps, cardConfig: undefined };
AlertTopPanelDetails.propTypes = {
  alertHeader: PropTypes.string,
  isLoading: PropTypes.bool,
  data: PropTypes.object,
  error: PropTypes.object,
  disableSelect: PropTypes.bool,
  isFromCM: PropTypes.bool,
  alertMetaSuffix: PropTypes.element,
  alertLevels: PropTypes.array,
  trmActionsData: PropTypes.array,
  // FIN-11542 Cluster 3 (Ganesh 2026-06-03): GS hide/rename for card fields
  cardConfig: PropTypes.object
};

export default AlertTopPanelDetails;
